<template>
  <BittsModal
    :hide-footer="true"
    :width="500"
    :use-mask-to-close="true"
    :loading="loading"
    class="c-offline-partner-google-sheets-connect"
    @closed="close"
  >
    <ConnectDataSource
      v-if="dataSource"
      :data-source="dataSource"
      @redirect-retrieved="authorize"
    >
      <template #submit-button="{ submit }">
        <div class="flex w-full justify-center">
          <BittsButton
            variant="outline"
            text="Authenticate with Google"
            data-testid="authenticate-with-google-button"
            @click="submit"
          >
            <template #icon>
              <img
                src="@/assets/google-icon.png"
                class="h-12 w-12 mr-12"
                alt=""
              />
            </template>
          </BittsButton>
        </div>
      </template>
    </ConnectDataSource>
  </BittsModal>
</template>

<script setup>
import { BittsButton, BittsModal } from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import ConnectDataSource from '@/components/data-sources/ConnectDataSource.vue';

import { crossbeamApi } from '@/api';
import { GOOGLE_SHEETS_DATA_SOURCE_TYPE } from '@/constants/data_sources';
import { captureException } from '@/errors';
import { useDataSourcesStore, useFlashesStore } from '@/stores';
import { saveOfflineSheetSetup } from '@/utils/offlineSheetSetup';

const route = useRoute();
const router = useRouter();
const dataSourcesStore = useDataSourcesStore();
const flashesStore = useFlashesStore();

useHead({ title: 'Connect Google Sheets - Crossbeam' });

const loading = ref(true);
const dataSource = ref(null);

const offlinePartnerId = computed(() => route.query?.offlinePartnerId);
const offlinePartnerUuid = computed(() => route.query?.offlinePartnerUuid);

onMounted(async () => {
  try {
    await dataSourcesStore.readySync;
    dataSource.value = dataSourcesStore.getDataSource(
      GOOGLE_SHEETS_DATA_SOURCE_TYPE,
    );
  } finally {
    loading.value = false;
  }
});

/* A malformed URL must not break the redirect; an empty state only means the
   callback cannot correlate and falls back to the partner */
function readOAuthState(authorizationUrl) {
  try {
    return new URL(authorizationUrl).searchParams.get('state') ?? '';
  } catch (err) {
    return '';
  }
}

/* No sheet is chosen yet - the partner picks one after Google consent, the same
   way the active org does */
async function authorize() {
  loading.value = true;
  try {
    const { data, error } = await crossbeamApi.POST(
      '/v0.1/offline-partners/{id}/google-sheets/feed',
      {
        params: { path: { id: offlinePartnerUuid.value } },
        body: {},
      },
    );
    if (error) throw error;

    saveOfflineSheetSetup({
      state: readOAuthState(data.authorization_url),
      offlinePartnerId: offlinePartnerId.value,
      offlinePartnerUuid: offlinePartnerUuid.value,
    });
    window.open(data.authorization_url, '_self');
  } catch (err) {
    captureException(err);
    flashesStore.addErrorFlash({
      message: 'Could not connect to Google Sheets',
      description: 'If the error persists contact support@crossbeam.com',
    });
    loading.value = false;
  }
}

async function close() {
  await router.push({
    name: 'partner_details',
    params: { partner_org_id: offlinePartnerId.value },
    query: { tab: 'data' },
  });
}
</script>
